import { fmtDate, fmtNum, fmtPct } from "@/lib/format";

const Cell = ({ label, value, testId }) => (
  <div className="border-b border-r border-slate-300 px-3 py-2">
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-semibold text-slate-900" data-testid={testId}>{value || "—"}</p>
  </div>
);

function SectionTable({ s, band = "bg-[#FAC090]" }) {
  const footage = s.key === "footage";
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-sm" data-testid={`doc-section-${s.key}`}>
        <thead>
          <tr className={`${band} text-left text-[11px] font-bold uppercase tracking-wide text-slate-900`}>
            <th className="border border-slate-400 px-2 py-2">{s.title}</th>
            <th className="border border-slate-400 px-2 py-2 text-center">{s.today}</th>
            <th className="border border-slate-400 px-2 py-2 text-center">{s.previous}</th>
            <th className="border border-slate-400 px-2 py-2 text-center">{s.total}</th>
            <th className="border border-slate-400 px-2 py-2 text-center">{footage ? s.extra : s.total_label}</th>
          </tr>
        </thead>
        <tbody>
          {s.rows.map((r, i) => (
            <tr key={r.key} className={r.today ? "bg-[#5C241E]/[0.04]" : ""}>
              <td className="border border-slate-300 px-2 py-1.5 font-semibold">{r.label}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center font-semibold">{fmtNum(r.today)}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center">{fmtNum(r.previous)}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center">{fmtNum(r.total)}</td>
              {footage ? (
                <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center">{r.has_percent ? fmtPct(r.percent) : "N/A"}</td>
              ) : i === 0 ? (
                <td rowSpan={s.rows.length} className="font-mono-num border border-slate-300 px-2 text-center text-lg font-bold">{fmtNum(s.total_value)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FacilityTable({ s }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] border-collapse text-sm" data-testid="doc-section-footage">
        <thead>
          <tr className="bg-[#C0C0C0] text-left text-[11px] font-bold uppercase tracking-wide text-slate-900">
            <th className="border border-slate-400 px-2 py-2">{s.title}</th>
            {["Unit of Measure", "Today Footage / Days", "Total Footage / Days", "Est. Completion", "Percent Complete"].map((h) => (
              <th key={h} className="border border-slate-400 px-2 py-2 text-center">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {s.rows.map((r) => (
            <tr key={r.key} className={r.today ? "bg-[#5C241E]/[0.04]" : ""}>
              <td className="border border-slate-300 px-2 py-1.5 font-semibold">{r.label}</td>
              <td className="border border-slate-300 px-2 py-1.5 text-center">{r.unit}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center font-semibold">{r.today ? fmtNum(r.today) : ""}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center">{fmtNum(r.total)}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center">{r.estimate ? fmtNum(r.estimate) : "TBD"}</td>
              <td className="font-mono-num border border-slate-300 px-2 py-1.5 text-center">{r.has_percent ? fmtPct(r.percent) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportDocument({ data }) {
  const { report: r, client, project, sections, completion, signature } = data;
  const facility = project.template === "facility";
  const band = facility ? "bg-[#C0C0C0]" : "bg-[#FAC090]";
  const hours = [r.start_time && r.end_time ? `${r.start_time} – ${r.end_time}` : "", r.total_hours != null ? `${fmtNum(r.total_hours)} hrs` : ""].filter(Boolean).join(" · ");
  return (
    <div className="panel overflow-hidden" data-testid="report-document">
      <div className={`${band} px-4 py-2 text-center font-display text-lg font-bold uppercase tracking-wide text-slate-900`}>Inspectors Daily Progress Report</div>
      <div className="grid grid-cols-2 border-l border-t border-slate-300 sm:grid-cols-4">
        <Cell label="Client" value={client.name} testId="doc-client" />
        <Cell label="Date" value={fmtDate(r.report_date)} testId="doc-date" />
        <Cell label="Project name" value={project.name} testId="doc-project" />
        <Cell label="Project completion" value={fmtPct(completion)} testId="doc-completion" />
        <Cell label={facility ? "Job AFE#" : "Job #"} value={project.job_number} />
        <Cell label="Inspector / Craft" value={`${r.employee_name} / ${r.craft || "Consultant"}`} testId="doc-employee" />
        <Cell label="Contractor" value={r.contractor || project.contractor} />
        <Cell label={facility ? "Job description" : "Pipe description"} value={r.pipe_description || project.pipe_description} />
        <Cell label="Weather" value={r.weather} />
        <Cell label="Hours" value={hours} testId="doc-hours" />
        <Cell label="Crew" value={[r.crew_size, (r.crew_names || []).join(", ")].filter(Boolean).join(" · ")} testId="doc-crew" />
        <Cell label={facility ? "Template" : "Total footage of pipeline"} value={facility ? "Facility / construction" : fmtNum(project.total_footage)} />
      </div>
      <div className="space-y-4 p-3 sm:p-4">
        {sections.map((s) => (facility && s.key === "footage" ? <FacilityTable key={s.key} s={s} /> : <SectionTable key={s.key} s={s} band={band} />))}
        <div>
          <p className="border-b border-slate-300 pb-1 font-display text-lg font-bold">COMMENTS:</p>
          <p className="whitespace-pre-wrap pt-2 text-[15px] leading-relaxed text-slate-800" data-testid="doc-comments">{r.comments || "—"}</p>
        </div>
        {r.issues && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-800">Delays / Issues</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-amber-900" data-testid="doc-issues">{r.issues}</p>
          </div>
        )}
        <div>
          <p className={`${band} px-2 py-1 font-display text-lg font-bold`}>SAFETY:</p>
          <p className="whitespace-pre-wrap pt-2 text-sm text-slate-700">{r.safety || "—"}</p>
        </div>
        <div className={`flex flex-wrap items-center gap-3 ${facility ? "bg-[#A6A6A6]" : "bg-[#FAC090]"} px-2 py-1.5 text-sm font-bold`}>
          <span>INSPECTOR SIGNATURE:</span><span data-testid="doc-signature">{signature}</span>
        </div>
      </div>
    </div>
  );
}
